import React, { createContext, useContext, useEffect } from "react";
import { SimpleProduct, SimpleSku, updateLatestProducts } from "@/services/api/order";
import { action, observable, reaction, makeObservable, actionBound } from "mobx";
import OrderTab from "@/scenes/OrderManagement/enum/OrderTab";
import type GlobalStore from "@/store";
import { isEmpty } from "@/utils";
import { CacheOrderProduct, HotProduct, HotProductSku } from "./type";
import {
    buildKey,
    getCacheOrderUpdateFlag,
    getOrderLatestProductsMemoize,
} from "@/scenes/OrderManagement/components/OrderTable/SearchPane/LinkedProductSearchFields/utils";
import { FormInstance, message } from "antd";
import TableSearchState from "@/scenes/OrderManagement/redux/states/TableSearchState";
import { groupBy } from "lodash";
import { axiosGroupByShopType } from "@/services/http/platformAxiosUtil";
import { useOrderManagementStore } from "@/scenes/OrderManagementForAllShop/stores/OrderManagementForAllShopStore";
import { useUpdate, useUpdateEffect } from "ahooks";
import OrderStatus from "@/enum/OrderStatus";
import OrderDTO from "@/dto/order/OrderDTO";

interface AutoHandleHotProductDataProps {
    shopId?: number;
    form: FormInstance<TableSearchState>;
    tab: OrderTab;
    linkedProductSearchFieldsStore: LinkedProductSearchFieldsStore;
}

export function useAutoHandleHotProductData(props: AutoHandleHotProductDataProps) {
    const { form, tab, linkedProductSearchFieldsStore, shopId } = props;

    const orderManagementStore = useOrderManagementStore();

    useEffect(() => {
        linkedProductSearchFieldsStore.injectFormInstance(form);
    }, [form]);

    useUpdateEffect(() => {
        /**
         * 重置联动组件表单数据和商品下拉数据
         */
        form.setFieldsValue({
            orderItemExternalIdValue: {
                externalIdFieldSkuKeys: undefined,
                prefixedExternalId: undefined,
                prefixedExternalIds: [],
                orderItemExternalId: "",
                isSelectAll: false,
                isIndeterminate: false,
            },
            orderItemTitleValue: {
                orderItemTitle: "",
                productKey: undefined,
                productKeys: [],
                titleFieldSkuKeys: undefined,
                isSelectAll: false,
                isIndeterminate: false,
            },
            skuPropertiesValue: {
                skuProperties: "",
                skuKey: undefined,
                skuKeys: [],
                skuPropertiesFieldSkuKeys: undefined,
                isSelectAll: false,
                isIndeterminate: false,
            },
        });

        linkedProductSearchFieldsStore.initializeMapsAndArrays();
    }, [orderManagementStore.shopIds?.join()]);

    useEffect(() => {
        linkedProductSearchFieldsStore.updateShopId(shopId);
        linkedProductSearchFieldsStore.updateTab(tab);
    }, [tab, shopId]);

    useEffect(() => {
        return reaction(
            () => {
                return orderManagementStore.table.orders?.map((order) => order.id).join();
            },
            () => {
                if (getCacheOrderUpdateFlag(orderManagementStore.table.form?.getFieldsValue() || {})) {
                    const orders = orderManagementStore.table.orders;

                    linkedProductSearchFieldsStore.handleCacheOrderProducts(orders);
                }
            },
        );
    }, []);
}

export const LinkedProductSearchFieldsStoreContext = createContext<LinkedProductSearchFieldsStore>(null);

export const useLinkedProductSearchFieldsStore = () => {
    const update = useUpdate();

    const LinkedProductSearchFieldsStore = useContext(LinkedProductSearchFieldsStoreContext);

    useEffect(() => {
        LinkedProductSearchFieldsStore.subscribeData(update);

        return () => {
            LinkedProductSearchFieldsStore.unsubscribeData(update);
        };
    }, []);

    return LinkedProductSearchFieldsStore;
};

export const LinkedProductSearchSimpleTitleKey = "__LinkedProductSearchSimpleTitleKey__";
export const TOP_SELLING_PRODUCT_PRINT = "__TOP_SELLING_PRODUCT_PRINT__";
export const LinkedProductSearchSkuSimpleTitleKey = "__LinkedProductSearchSkuSimpleTitleKey__";
export const LinkedProductSearchTitlePicKey = "__LinkedProductSearchTitlePicKey__";
/**
 * 联动商品搜索store
 */
export default class LinkedProductSearchFieldsStore {
    constructor(public globalStore: typeof GlobalStore) {
        makeObservable(this, {
            updateShopId: actionBound,
            tab: observable,
            updateTab: actionBound,
            hasPendingShipmentOrder: observable,
            hasShippedOrder: observable,
            hasSuccessOrder: observable,
            loading: observable,
            loadProductSimpleTitle: observable,
            loadSkuSimpleTitle: observable,
            loadProductPicture: observable,
            enabledTopSellingProductPrinting: observable,
            getShopIds: actionBound,
            subscribeData: actionBound,
            unsubscribeData: actionBound,
            notifyDataChange: actionBound,
            injectFormInstance: actionBound,
            handleProcessProductInfos: actionBound,
            initializeMapsAndArrays: actionBound,
            processProductsAndSkus: actionBound,
            createAndMapSkus: actionBound,
            createProductSku: actionBound,
            mapSkuProperties: actionBound,
            createHotProduct: action,
            mapHotProduct: actionBound,
            updateCountOnTopSelling: actionBound,
            updateLoadProductSimpleTitle: actionBound,
            updateLoadSkuSimpleTitle: actionBound,
            updateLoadProductPicture: actionBound,
            handleCacheOrderProducts: actionBound,
            loadProducts: actionBound,
            switchTopSellingProductPrinting: actionBound,
            initTopSellingProductPrinting: actionBound,
            onFocusLoad: actionBound,
            updateLatestProducts: actionBound,
            updateLatestProductsByShopIds: actionBound,
        });
        const needSimpleTitle = localStorage.getItem(LinkedProductSearchSimpleTitleKey);
        const needSkuSimpleTitle = localStorage.getItem(LinkedProductSearchSkuSimpleTitleKey);
        const showTitlePic = localStorage.getItem(LinkedProductSearchTitlePicKey);

        if (needSimpleTitle) {
            this.loadProductSimpleTitle = true;
        }

        if (needSkuSimpleTitle) {
            this.loadSkuSimpleTitle = true;
        }

        if (showTitlePic === "2") {
            this.loadProductPicture = false;
        }
    }

    shopId: number;

    updateShopId(shopId: number) {
        this.shopId = shopId;
    }

    tab: OrderTab = null;

    updateTab(tab: OrderTab) {
        this.tab = tab;

        this.hasPendingShipmentOrder = [
            OrderTab.PENDING_SHIPMENT,
            OrderTab.ON_HOLD,
            OrderTab.PENDING_SHIPMENT_REFUNDING,
        ].includes(tab);

        this.hasShippedOrder = OrderTab.SHIPPED === tab;
        this.hasSuccessOrder = OrderTab.SUCCESS === tab;
    }

    hasPendingShipmentOrder = false;

    hasShippedOrder = false;

    hasSuccessOrder = false;

    loading = false;

    loadProductSimpleTitle = false;

    loadSkuSimpleTitle = false;

    loadProductPicture = true;

    simpleProducts: SimpleProduct[] = [];

    enabledTopSellingProductPrinting = false;

    productSkus: HotProductSku[] = [];

    products: HotProduct[] = [];

    cacheOrderProducts: CacheOrderProduct[] = [];

    /** 相同sku属性的productSku */
    sameSkuPropertiesMap = new Map<string, HotProductSku[]>();

    /** 相同的sku编码 */
    sameSkuExternalIdMap = new Map<string, HotProductSku[]>();

    /** 相同的product编码 */
    sameProductExternalIdMap = new Map<string, HotProduct[]>();

    productSkuMap = new Map<string, HotProductSku>();

    productMap = new Map<string, HotProduct>();

    form: FormInstance<TableSearchState>;

    listener = [];

    getShopIds() {
        /** 单店铺场景 */
        if (this.shopId) {
            return [this.shopId];
        }

        /** 跨店铺场景 */
        const { shopIds } = this.form.getFieldsValue();

        return shopIds;
    }

    subscribeData(cb) {
        this.listener.push(cb);
    }

    unsubscribeData(cb) {
        this.listener = this.listener.filter((fn) => fn !== cb);
    }

    notifyDataChange() {
        this.listener.forEach((fn) => fn());
    }

    injectFormInstance(form: FormInstance<TableSearchState>) {
        this.form = form;
    }

    async handleProcessProductInfos() {
        this.initializeMapsAndArrays();
        this.processProductsAndSkus();
        this.updateCountOnTopSelling();

        // 发布
        this.notifyDataChange();
    }

    initializeMapsAndArrays() {
        this.products = [];
        this.productSkus = [];
        this.sameSkuPropertiesMap = new Map();
        this.sameSkuExternalIdMap = new Map();
        this.sameProductExternalIdMap = new Map();
        this.productSkuMap = new Map();
        this.productMap = new Map();
    }

    processProductsAndSkus() {
        this.products = this.simpleProducts.map((product) => {
            const skus = this.createAndMapSkus(product);
            return this.createHotProduct(product, skus);
        });
    }

    createAndMapSkus(product: SimpleProduct) {
        return product.skus.map((sku) => {
            const productSku = this.createProductSku(product, sku);

            this.mapSkuProperties(productSku);

            return productSku;
        });
    }

    createProductSku(product: SimpleProduct, sku: SimpleSku): HotProductSku {
        return {
            title: product.title,
            originalId: sku.originalId,
            productOriginalId: product.originalId,
            skuProperties: this.loadSkuSimpleTitle && !isEmpty(sku.simpleTitle) ? sku.simpleTitle : sku.propertiesName,
            count: 0,
            skuExternalId: sku.externalId,
            picture: sku.picture,
            shopId: product.shopId,
            productKey: buildKey(product.shopId, product.originalId),
            skuKey: buildKey(product.shopId, sku.id),
        };
    }

    mapSkuProperties(productSku: HotProductSku) {
        this.productSkuMap.set(productSku.skuKey, productSku);
        this.productSkus.push(productSku);

        if (!this.sameSkuPropertiesMap.has(productSku.skuProperties)) {
            this.sameSkuPropertiesMap.set(productSku.skuProperties, []);
        }

        if (!this.sameSkuExternalIdMap.has(productSku.skuExternalId)) {
            this.sameSkuExternalIdMap.set(productSku.skuExternalId, []);
        }

        const sameSkuPropertiesList = this.sameSkuPropertiesMap.get(productSku.skuProperties);
        const sameSkuExternalIdList = this.sameSkuExternalIdMap.get(productSku.skuExternalId);

        sameSkuPropertiesList.push(productSku);
        sameSkuExternalIdList.push(productSku);
    }

    createHotProduct(product: SimpleProduct, skus: HotProductSku[]) {
        const hotProduct: HotProduct = {
            title: this.loadProductSimpleTitle && !isEmpty(product.simpleTitle) ? product.simpleTitle : product.title,
            originalId: product.originalId,
            count: 0,
            productExternalId: product.externalId,
            skus,
            shopId: product.shopId,
            productKey: buildKey(product.shopId, product.originalId),
            picture: product.picture,
            titles: product.titles || [],
        };

        this.mapHotProduct(product, hotProduct);
        return hotProduct;
    }

    mapHotProduct(product: SimpleProduct, hotProduct: HotProduct) {
        this.productMap.set(hotProduct.productKey, hotProduct);

        if (product.externalId) {
            if (!this.sameProductExternalIdMap.has(product.externalId)) {
                this.sameProductExternalIdMap.set(product.externalId, []);
            }

            const sameProductExternalIdList = this.sameProductExternalIdMap.get(product.externalId);

            sameProductExternalIdList.push(hotProduct);
        }
    }

    updateCountOnTopSelling() {
        if (!this.enabledTopSellingProductPrinting) return;

        // 如果爆款打单开启，执行计算数量逻辑
        const hotProductMapping: Map<string, HotProduct> = new Map();
        const hotProductSkuMapping: Map<string, Omit<HotProductSku, "skuKey">> = new Map();

        this.cacheOrderProducts.forEach(
            ({
                productOriginalId,
                title,
                skuProperties,
                skuOriginalId,
                status,
                productExternalId,
                skuExternalId,
                picture,
                quantity,
                shopId,
            }) => {
                if (this.tab === OrderTab.SHIPPED) {
                    // 已发货下支持统计爆款
                } else {
                    // break
                    if (status !== OrderStatus.PENDING_SHIPMENT) {
                        return;
                    }
                }

                // break
                if (!productOriginalId) {
                    return;
                }

                const productKey = buildKey(shopId, productOriginalId);

                let hotProduct = hotProductMapping.get(productKey);

                if (!hotProduct) {
                    hotProduct = {
                        originalId: productOriginalId,
                        title,
                        count: quantity || 1,
                        productExternalId,
                        skus: [],
                        shopId,
                        productKey: productKey,
                        picture: null,
                        titles: [],
                    };

                    hotProductMapping.set(productKey, hotProduct);
                } else {
                    hotProduct.count += quantity || 1;
                }

                // break
                if (!skuOriginalId) {
                    return;
                }

                /** 这里的skuKey与模型中的key不一样，但是仅用于当前函数中，方便计算读取Sku */
                const skuKey = `${productKey}|${skuOriginalId}`;

                let hotProductSku = hotProductSkuMapping.get(skuKey);

                if (!hotProductSku) {
                    hotProductSku = {
                        productOriginalId,
                        originalId: skuOriginalId,
                        title,
                        skuProperties,
                        count: quantity || 1,
                        skuExternalId,
                        picture,
                        shopId,
                        productKey,
                    };

                    hotProductSkuMapping.set(skuKey, hotProductSku);
                } else {
                    hotProductSku.count += quantity || 1;
                }
            },
        );

        this.products.forEach((product) => {
            const hotProduct = hotProductMapping.get(product.productKey);

            if (hotProduct) {
                product.count = hotProduct.count;
            }
        });

        this.productSkus.forEach((sku) => {
            const skuKey = `${sku.productKey}|${sku.originalId}`;
            const hotProductSku = hotProductSkuMapping.get(skuKey);

            if (hotProductSku) {
                sku.count = hotProductSku.count;
            }
        });

        this.products = this.products.sort((a, b) => b.count - a.count);
        this.productSkus = this.productSkus.sort((a, b) => b.count - a.count);
    }

    async updateLoadProductSimpleTitle(value: boolean) {
        this.loadProductSimpleTitle = value;

        if (value) {
            localStorage.setItem(LinkedProductSearchSimpleTitleKey, "1");

            await this.loadProducts();
        } else {
            localStorage.removeItem(LinkedProductSearchSimpleTitleKey);
        }

        await this.handleProcessProductInfos();
    }

    async updateLoadSkuSimpleTitle(value: boolean) {
        this.loadSkuSimpleTitle = value;

        if (value) {
            localStorage.setItem(LinkedProductSearchSkuSimpleTitleKey, "1");

            await this.loadProducts();
        } else {
            localStorage.removeItem(LinkedProductSearchSkuSimpleTitleKey);
        }

        await this.handleProcessProductInfos();
    }

    async updateLoadProductPicture(checked: boolean) {
        this.loadProductPicture = checked;

        if (checked) {
            localStorage.setItem(LinkedProductSearchTitlePicKey, "1");
            await this.loadProducts();
        } else {
            localStorage.setItem(LinkedProductSearchTitlePicKey, "2");
        }

        await this.handleProcessProductInfos();
    }

    handleCacheOrderProducts(orders: OrderDTO[]) {
        if (!orders) return;

        const cacheOrderProducts: CacheOrderProduct[] = [];

        orders.forEach((order) => {
            const { shopId } = order;

            order.orderItems.forEach((orderItem) => {
                const {
                    productOriginalId,
                    title,
                    skuProperties,
                    skuOriginalId,
                    status,
                    productExternalId,
                    skuExternalId,
                    picture,
                    quantity,
                } = orderItem;

                if (this.tab === OrderTab.SHIPPED) {
                    // 已发货下支持统计爆款
                } else {
                    // break
                    if (status !== OrderStatus.PENDING_SHIPMENT) {
                        return;
                    }
                }

                // break
                if (!productOriginalId) {
                    return;
                }

                cacheOrderProducts.push({
                    productOriginalId,
                    title,
                    skuProperties,
                    skuOriginalId,
                    status,
                    productExternalId,
                    skuExternalId,
                    picture,
                    quantity,
                    shopId,
                });
            });
        });

        this.cacheOrderProducts = cacheOrderProducts;
    }

    async loadProducts() {
        this.loading = true;

        try {
            const shopIds = this.getShopIds();

            if (!shopIds?.length) {
                return;
            }

            const simpleProducts: SimpleProduct[] = [];

            const groupedShopIds = groupBy(shopIds, (shopId) => {
                const shopType = this.globalStore.shopMapping.get(shopId)?.type;

                return axiosGroupByShopType(shopType);
            });

            for (const groupShopIds of Object.values(groupedShopIds)) {
                const res = await getOrderLatestProductsMemoize({
                    shopIds: groupShopIds,
                    hasPendingShipmentOrder: this.hasPendingShipmentOrder,
                    loadProductSimpleTitle: this.loadProductSimpleTitle,
                    loadSkuSimpleTitle: this.loadSkuSimpleTitle,
                    loadProductPicture: this.loadProductPicture,
                    hasShippedOrder: this.hasShippedOrder,
                    hasSuccessOrder: this.hasSuccessOrder,
                });

                simpleProducts.push(...res);
            }

            this.simpleProducts = simpleProducts;
        } catch (error) {
            console.error(error);
        } finally {
            this.loading = false;
        }
    }

    async switchTopSellingProductPrinting(checked: boolean) {
        this.enabledTopSellingProductPrinting = checked;

        localStorage.setItem(TOP_SELLING_PRODUCT_PRINT, checked ? "1" : "0");

        this.handleProcessProductInfos();
    }

    async initTopSellingProductPrinting() {
        const cacheTopSellingProductPrinting = localStorage.getItem(TOP_SELLING_PRODUCT_PRINT);

        this.enabledTopSellingProductPrinting = cacheTopSellingProductPrinting === "1";
    }

    async onFocusLoad() {
        await this.loadProducts();

        await this.initTopSellingProductPrinting();

        await this.handleProcessProductInfos();
    }

    /**
     * 更新最新的商品信息
     */
    async updateLatestProducts() {
        if (!this.shopId) {
            return;
        }

        await updateLatestProducts(this.shopId);

        getOrderLatestProductsMemoize.cache.clear();

        await this.loadProducts();
    }

    async updateLatestProductsByShopIds() {
        const shopIds = this.getShopIds();

        if (!shopIds.length) {
            return;
        }

        let allSuccess = true;

        for (const shopId of shopIds) {
            try {
                await updateLatestProducts(shopId, true);
            } catch (e) {
                const shop = this.globalStore.shopMapping.get(shopId);
                message.error(`${shop.name}更新失败:${e.message}`, 5);
                allSuccess = false;
            }
        }

        if (allSuccess) {
            message.success("更新商品成功");
        }

        getOrderLatestProductsMemoize.cache.clear();

        await this.loadProducts();
    }
}
